<template>
  <div class="edit-study-set">
    <organization-breadcrumb :items="[$t('study_sets.curriculum'), $t('read_aloud.title')]" v-show="showBreadcrumb" />
    <div id="edit-study-set-page">
      <el-row type="flex" justify="center">
        <el-col :xs="24" :md="14" :lg="18" :xl="22">
          <h1 class="blue-color" style="text-align: center" v-if="createStudySetSteps.active == 1">
            {{ $t("read_aloud.edit_read_aloud") }}
          </h1>
          <h1 class="blue-color" style="text-align: center" v-else-if="createStudySetSteps.active == 2">
            {{ $t("read_aloud.review_confirm") }}
          </h1>
          <h1 class="blue-color" style="text-align: center" v-else-if="createStudySetSteps.active == 3">
            {{ $t("read_aloud.review_confirm") }}
          </h1>
          <h1 class="blue-color" style="text-align: center" v-else>
            <!-- {{ $t("read_aloud.your_study_set") }} -->
            Edit Read Aloud
          </h1>

          <div>
            <div class="study-set-edit-main">
              <study-set-create-detail
                :componentStep="[1, 2]"
                :currentStep="createStudySetSteps.active"
                :editMode="editMode"
                :isAdmin="true"
                :organizationType="currentOrganizationType"
                ref="studysetdetail"
                v-if="createStudySetSteps.active === 1"
                v-on:next-step="handleNextStep"
                v-on:previous-step="handlePreviousStep"
                @generate="handleGenerate"
              />
              <study-set-overview
                :componentStep="2"
                :currentStep="createStudySetSteps.active"
                :organizationId="currentOrganizationId"
                :organizationType="currentOrganizationType"
                :studysetDetails="buildingStudySet"
                v-if="createStudySetSteps.active === 2 || createStudySetSteps.active === 3"
                v-on:cancel="handleCancel"
                v-on:edit-step="handleEdit"
                v-on:last-step="lastStep"
                v-on:next-step="handleConfirmDoneEditStudySet"
              />
            </div>
            <div>
              <study-set-edit-footer
                :previousShowIcon="false"
                :previousText="$t('common.cancel')"
                :primaryAction="clickContinue"
                :primaryPreviousAction="handleCancel"
                :primaryPreviousActive="true"
                :primaryPreviousText="$t('common.cancel')"
                primaryText="Next"
                v-if="createStudySetSteps.active === 1"
              />
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
    <leave-edit-study-set-modal
      :dialogVisible="modals.showLeaveEditStudySetModal"
      append-to-body
      v-on:closed="modals.showLeaveEditStudySetModal = false"
      v-on:leave="handleConfirmCancel"
    />
  </div>
</template>

<script>
import LeaveEditStudySetModal from "@/components/modals/studysets/LeaveEditStudySetModal";
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import StudySetCreateDetail from "./create-read-aloud/ReadAloudCreateDetail";
import StudySetEditFooter from "./edit-studysets/StudySetEditFooter";
import StudySetOverview from "./edit-studysets/StudySetOverview";
import { DefaultShareableOptions } from "@/constants/studySets";
import { mapGetters, mapActions, mapState } from "vuex";
import { warningMessage } from "@/utils/messages";

export default {
  components: {
    LeaveEditStudySetModal,
    OrganizationBreadcrumb,
    StudySetCreateDetail,
    StudySetEditFooter,
    StudySetOverview,
  },
  props: {
    studySetId: {
      type: [String, Number],
      default: null,
    },
    showBreadcrumb: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      modals: {
        showLeaveEditStudySetModal: false,
      },
      errors: {},
      primaryLoading: false,
      loading: null,
      shareableOptions: DefaultShareableOptions,
      createStudySetSteps: {
        active: 1,
        steps: [
          {
            title: this.$t("studysets.create_studyset_flow.details"),
          },
          {
            title: this.$t("study_sets.review_confirm"),
          },
        ],
      },
      editMode: false,
      isConfirmCancel: false,
    };
  },
  watch: {
    isLoadingEdit(status) {
      if (status) {
        this.loading = this.$loading({
          lock: true,
          text: this.$t("common.loading"),
          spinner: "el-icon-loading",
          background: "rgba(0, 0, 0, 0.7)",
        });
      } else {
        this.loading && this.loading.close();
      }
    },
  },
  computed: {
    ...mapGetters({
      categories: "studysets/getStudySetCategories",
      currentOrganizationId: "organization/currentOrganizationId",
      currentOrganizationType: "organization/currentOrganizationType",
      getCurrentStudySet: "studysets/getCurrentStudySet",
      buildingStudySet: "studysets/getBuildingStudySet",
      getDetailReadAloud: "readAlouds/getDetailReadAloud",
    }),
    ...mapState("readAlouds", ["editReadAloud", "cloneWarmUp", "isLoadingEdit"]),
    membershipSelectedDisplayName() {
      if (this.buildingStudySet && this.buildingStudySet.name) {
        return this.buildingStudySet.name;
      }
      return "";
    },
    countOfPhrases() {
      if (this.buildingStudySet && this.buildingStudySet.phrases) {
        return this.buildingStudySet.phrases.length;
      }
      return 0;
    },
    studySetDetails() {
      return this.buildingStudySet;
    },
  },
  async mounted() {
    try {
      const currentStudySet = this.getCurrentStudySet;
      await this.updateBuildingStudySet({
        name: currentStudySet.name,
        category: currentStudySet.category,
        shareable: currentStudySet.shareable,
        phrases: currentStudySet.phrases.map(x => {
          return { text: x };
        }),
        id: currentStudySet.id,
      });
    } catch (error) {
      return warningMessage("error");
    }
  },
  methods: {
    ...mapActions({
      updateBuildingStudySet: "studysets/updateBuildingStudySet",
      updateStudySet: "studysets/updateStudySet",
      checkStudySetPermissionAction: "studysets/checkStudySetPermission",
    }),
    ...mapActions("readAlouds", [
      "resetEditReadAloud",
      "getWarmUpStudySets",
      "updateEditReadAloud",
      "setFinalWarmUpStudySets",
      "changeCloneWarmUp",
      "cloneWarmUpAction",
      "changeEditSuccess",
    ]),
    handleChangeWarmup(list) {
      this.changeCloneWarmUp(list);
    },
    async handleGenerate() {
      // this.createStudySetSteps.active = 2;
      const payload = {
        max_items: 50,
        text: this.editReadAloud.text,
      };

      try {
        this.loading = this.$loading({
          lock: true,
          text: this.$t("common.loading"),
          spinner: "el-icon-loading",
          background: "rgba(0, 0, 0, 0.7)",
        });
        await this.getWarmUpStudySets(payload);
      } finally {
        this.loading && this.loading.close();
      }
    },
    hasHistory() {
      return window.history.length > 2;
    },
    handleNextStep(activeStep) {
      this.createStudySetSteps.active = activeStep + 1;
      if (this.cloneWarmUp.length) {
        this.setFinalWarmUpStudySets(this.cloneWarmUp);
      }
    },
    handlePreviousStep() {
      const activeStep = this.createStudySetSteps.active;
      this.createStudySetSteps.active = activeStep - 1;
    },
    handleCancel() {
      this.modals.showLeaveEditStudySetModal = true;
    },
    handleConfirmCancel() {
      this.isConfirmCancel = true;

      if (this.$route.name === "assignment-library-detail") {
        return this.$router.go(this.$router.currentRoute);
      }

      this.$store.dispatch("readAlouds/resetBuildingStudySet");

      return this.$router.push({
        name: "read-aloud-details",
      });
    },
    handleConfirmDoneEditStudySet() {
      this.isConfirmCancel = true;

      if (this.$route.name === "assignment-library-detail") {
        return this.$router.go(this.$router.currentRoute);
      }

      this.$store.dispatch("readAlouds/resetBuildingStudySet");
      this.resetEditReadAloud();
      this.changeEditSuccess(false);
      this.changeCloneWarmUp([]);

      return this.$router.push({
        name: "read-aloud",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    handleEdit() {
      this.createStudySetSteps.active = 1;
      this.editMode = true;
    },
    clickContinue() {
      switch (this.createStudySetSteps.active) {
        case 1:
          this.$refs.studysetdetail.handleContinueClick();
          this.cloneWarmUpAction();
          break;
        case 2:
          this.modals.showModalSpellCheck = true;

          this.$refs.studysetdetail.handleContinueClick();
          break;
        default:
          return;
      }
    },
    lastStep() {
      this.createStudySetSteps.active = 3;
    },
    async handleCheckStudySetPermission(studySetId) {
      try {
        const checkedStudySets = await this.checkStudySetPermissionAction([studySetId]);
        if (checkedStudySets["view"].includes(studySetId)) {
          return this.handleConfirmCancel();
        }
      } catch (error) {
        error;
      }
    },
  },
  destroyed() {
    this.$store.dispatch("readAlouds/resetBuildingStudySet");
  },
  async beforeRouteLeave(to, from, next) {
    if (this.isConfirmCancel) {
      next();
    } else {
      this.modals.showLeaveEditStudySetModal = true;
      next(false);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../scss/_colors.scss";

.back-text {
  color: $hyperlink;
  font-weight: 600;
}

.edit-study-set {
  width: 100%;
}
#edit-study-set-page {
  margin: 24px;
  @media only screen and (max-width: 768px) {
    margin: 0 30px;
  }
  h1 {
    border: 0;
    font-size: 30px;
    line-height: 45px;
    font-weight: 700;
  }
  .study-set-edit-steps {
    margin-top: 25px;
  }
  .study-set-edit-main {
    background-color: white;
    padding: 40px 0px 10px 0px;
  }
  ::v-deep .base-create-header {
    padding-bottom: 34px;
  }
  ::v-deep .circle-step {
    border: 1px solid #e6e6e6;
    box-sizing: border-box;
    border-radius: 23px;
    width: 40px;
    height: 40px;
    display: inline-flex;
    justify-content: center;
    padding-top: 8px;
    margin-right: 16px;
  }
}

.success-info {
  .success {
    font-weight: bold;
    font-size: 22px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #46c779;
    line-height: 30px;
  }
  p {
    margin-top: 20px;
    font-weight: 300;
    font-size: 18px;
    color: #546873;
    margin: 0;
  }
}
::v-deep .create-study-set-com-disable {
  opacity: 0.5;
}
.line-separate {
  border: 1px solid#E6E6E6;
  margin-bottom: 32px;
}
</style>
